// ✅ Берём базовый URL из window.ENV (сервер) или fallback на локальный
const IM_CON_API_URL =
  (typeof window !== 'undefined' && window.ENV && window.ENV.imcon)
    ? `${window.ENV.imcon}/api`
    : 'http://localhost:4001/api';

class KratosAdminClient {
  constructor(baseUrl = IM_CON_API_URL) {
    this.baseUrl = baseUrl;
  }

  async request(endpoint, options = {}) {
    const fullUrl = `${this.baseUrl}${endpoint}`;

    console.log('👑 ImCon API request:', {
      url: fullUrl,
      method: options.method || 'GET'
    });

    const config = {
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(fullUrl, config);

      console.log('📨 ImCon API response:', {
        status: response.status,
        statusText: response.statusText,
        endpoint,
        ok: response.ok
      });

      // Проверяем Content-Type
      const contentType = response.headers.get('content-type');

      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Expected JSON but got:', contentType, text.substring(0, 200));
        throw new Error(`Server returned non-JSON response: ${contentType}`);
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      return data.data;
    } catch (error) {
      console.error('💥 ImCon API request failed:', {
        message: error.message,
        endpoint,
        baseUrl: this.baseUrl
      });
      throw error;
    }
  }

  // Получить список всех пользователей
  async listIdentities(page = 1, perPage = 1000) {
    return await this.request(`/users?page=${page}&per_page=${perPage}`);
  }

  // Создать нового пользователя
  async createIdentity(identityData) {
    return await this.request('/users', {
      method: 'POST',
      body: JSON.stringify({ user: identityData }),
    });
  }

  // Обновить пользователя
  async updateIdentity(id, traits, state = 'active') {
    return await this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        user: {
          ...traits,
          state: state
        }
      }),
    });
  }

  // Удалить пользователя
  async deleteIdentity(id) {
    return await this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Переключить состояние пользователя
  async toggleUserState(id, state) {
    return await this.request(`/users/${id}/state`, {
      method: 'PATCH',
      body: JSON.stringify({ state }),
    });
  }

  // Получить пользователя по ID (если нужно)
  async getIdentity(id) {
    return await this.request(`/users/${id}`);
  }

  /**
   * ✅ Native Flow login через ImCon → Kratos Public API
   * Обходит проблему Origin header и CORS
   * Возвращает session_token напрямую от Kratos
   */
  async nativeLogin(flowId, identifier, password, csrfToken = '') {
    return await this.request('/login-native', {
      method: 'POST',
      body: JSON.stringify({
        flow_id: flowId,
        identifier: identifier,
        password: password,
        csrf_token: csrfToken
      })
    });
  }

  /**
   * ✅ Получение Native Flow через ImCon → Kratos Public API
   */
  async getLoginFlow() {
    return await this.request('/login-flow');
  }
}

// Создаем экземпляр
export const kratosAdmin = new KratosAdminClient();

// Функция для проверки существования пользователя
export const checkUserExists = async (email) => {
  try {
    const identities = await kratosAdmin.listIdentities(1, 1000);
    return identities.find(identity =>
      identity.traits?.email?.toLowerCase() === email.toLowerCase()
    );
  } catch (error) {
    console.error('Error checking user existence:', error);
    return null;
  }
};

// Создание пользователя через ImCon
export const createUserDirectly = async (email, password, traits = {}) => {
  console.log('👑 Creating user via ImCon:', email);

  // Проверяем, не существует ли уже пользователь
  const existingUser = await checkUserExists(email);
  if (existingUser) {
    throw new Error(`Пользователь с email ${email} уже существует`);
  }

  const identityData = {
    schema_id: 'default',
    traits: {
      email: email,
      name: traits.name || email.split('@')[0],
      ...traits
    },
    credentials: {
      password: {
        config: {
          password: password,
        },
      },
    },
    state: 'active',
  };

  console.log('📦 Sending identity data to ImCon:', identityData);
  const result = await kratosAdmin.createIdentity(identityData);
  console.log('✅ User created via ImCon:', result);

  return result;
};

// Функция для массового получения пользователей
export const getAllUsers = async () => {
  try {
    const identities = await kratosAdmin.listIdentities(1, 1000);
    console.log(`📊 Loaded ${identities.length} users via ImCon`);
    return identities;
  } catch (error) {
    console.error('Error loading users via ImCon:', error);
    throw error;
  }
};

// Проверка доступности ImCon API
export const checkImConHealth = async () => {
  try {
    const response = await fetch(`${IM_CON_API_URL}/health`);
    const data = await response.json();
    console.log('🏥 ImCon health:', data);
    return { status: 'healthy', data: data };
  } catch (error) {
    console.error('❌ ImCon health check failed:', error);
    return { status: 'unhealthy', error: error.message };
  }
};

// Диагностика соединения
export const diagnoseConnection = async () => {
  try {
    console.log('🔍 Starting connection diagnosis...');

    // 1. Проверяем базовый health endpoint ImCon
    const healthResponse = await fetch(`${IM_CON_API_URL}/health`);
    console.log('🏥 ImCon health check:', healthResponse.status);

    // 2. Проверяем доступность users endpoint
    const usersResponse = await fetch(`${IM_CON_API_URL}/users`);
    console.log('👥 Users endpoint:', usersResponse.status);

    return {
      imConHealth: healthResponse.status,
      usersEndpoint: usersResponse.status
    };
  } catch (error) {
    console.error('❌ Connection diagnosis failed:', error);
    return { error: error.message };
  }
};

export default {
  kratosAdmin,
  checkUserExists,
  createUserDirectly,
  getAllUsers,
  checkImConHealth,
  diagnoseConnection
};
